import * as THREE from 'three';
import { EFFECT_LAYER } from '../enhanced-effects';

// ---------------------------------------------------------------------------
// 3. Heat Haze — multi-frequency distortion plane above ground
// ---------------------------------------------------------------------------

export function createHeatHaze(scene: THREE.Scene): {
  mesh: THREE.Mesh;
  update: (dt: number) => void;
  dispose: () => void;
} {
  const geo = new THREE.PlaneGeometry(200, 200, 1, 1);
  geo.rotateX(-Math.PI / 2);

  const uniforms = {
    uTime: { value: 0.0 },
  };

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
    side: THREE.DoubleSide,
    uniforms,
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      varying float vDist;
      varying float vHeight;
      void main() {
        vUv = uv;
        vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
        vDist = -mvPos.z;
        // World Y for height-based intensity
        vHeight = (modelMatrix * vec4(position, 1.0)).y;
        gl_Position = projectionMatrix * mvPos;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      varying vec2 vUv;
      varying float vDist;
      varying float vHeight;
      void main() {
        // Only visible at medium-far distances
        float distFade = smoothstep(8.0, 30.0, vDist) * smoothstep(120.0, 55.0, vDist);

        // Three-frequency distortion for organic look
        float wave1 = sin(vUv.x * 30.0 + uTime * 2.2) * sin(vUv.y * 25.0 + uTime * 1.6);
        float wave2 = sin(vUv.x * 18.0 - uTime * 1.4) * cos(vUv.y * 22.0 + uTime * 0.9);
        float wave3 = sin(vUv.x * 45.0 + uTime * 3.1) * sin(vUv.y * 38.0 - uTime * 2.3) * 0.5;
        float shimmer = (wave1 + wave2 + wave3) * 0.4;

        // Height variation: stronger closer to ground
        float heightFade = smoothstep(2.0, 0.0, vHeight);

        // Increased alpha: 0.10 base, stronger near ground
        float baseAlpha = 0.10 + heightFade * 0.04;
        float alpha = abs(shimmer) * baseAlpha * distFade;

        vec3 color = vec3(0.95, 0.90, 0.80);
        gl_FragColor = vec4(color, alpha);
      }
    `,
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.userData.isEffect = true;
  mesh.layers.set(EFFECT_LAYER);
  mesh.position.y = 0.3;
  mesh.renderOrder = 100;
  scene.add(mesh);

  return {
    mesh,
    update(dt: number) {
      uniforms.uTime.value += dt;
    },
    dispose() {
      scene.remove(mesh);
      geo.dispose();
      mat.dispose();
    },
  };
}
